"use client";

import React, { useEffect, useMemo, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { useAppSelector } from "@/app/lib/hooks";
import { useParams } from "next/navigation";
import type { Route } from "next";
import { fetchExchangeRate } from "@/utils/currencyUtils";
import { useTranslation } from "react-i18next";

interface Props {
  onClose?: () => void;
}

const GuydeezCoins: React.FC<Props> = ({ onClose }) => {
  const { t } = useTranslation("guydeez");
  const user = useAppSelector((s) => s.auth.user);
  const coins = typeof user?.guydeezCoin === 'number' ? user.guydeezCoin : Number(user?.guydeezCoin || 0);
  const params = useParams<{ currency?: string }>();
  const selectedCurrency = (params?.currency ? String(params.currency) : 'EUR').toUpperCase();
  const [rate, setRate] = useState<number | null>(selectedCurrency === 'EUR' ? 1 : null);
  const [rateError, setRateError] = useState(false);

  useEffect(() => {
    let ignore = false;
    const load = async () => {
      if (selectedCurrency === 'EUR') {
        if (!ignore) { setRate(1); setRateError(false); }
        return;
      }
      const fx = await fetchExchangeRate('EUR', selectedCurrency);
      if (ignore) return;
      if (typeof fx === 'number') { setRate(fx); setRateError(false); }
      else { setRate(1); setRateError(true); }
    };
    load();
    return () => { ignore = true; };
  }, [selectedCurrency]);

  const amountInCurrency = useMemo(() => {
    if (!coins) return 0;
    if (selectedCurrency === 'EUR') return coins / 100;
    if (!rate) return 0;
    return (coins / 100) * rate; // coins/100 in EUR → convert to selected
  }, [coins, rate, selectedCurrency]);

  const formattedAmount = useMemo(() => (
    Number.isFinite(amountInCurrency) ? amountInCurrency.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0'
  ), [amountInCurrency]);

  return (
    <div className="space-y-1">
      {/* Line 1: 593Gift Card */}
      <div className="text-sm font-semibold flex items-center gap-2">
        <Image
          src="/images/guydeezcoin.svg"
          alt="Gift Card"
          width={20}
          height={20}
          className="w-5 h-5"
        />
        <span>{coins.toLocaleString()} {t('menu_guydeez_coin_balance')}</span>
      </div>
      {/* Line 2: ≈ 7 USD */}
      <div className="text-xs text-neutral-500 dark:text-neutral-300">
        {t('menu_guydeez_coin_value', { amount: formattedAmount, code: selectedCurrency })}
      </div>
      {/* Links */}
      <div className="text-xs">
        <p>
          <Link href={"/gift-cards" as Route} className="text-primary underline" onClick={onClose}>
            {t('menu_buy_guydeez_coins')}
          </Link>
        </p>
        <p>
          <Link href={"/redeem" as Route} className="text-primary underline" onClick={onClose}>
            {t('redeem_go_to_redeem_code')}
          </Link>
        </p>
      </div>
      {rateError && selectedCurrency !== 'EUR' && (
        <div className="text-xs text-red-500">{t('menu_guydeez_coin_rate_error')}</div>
      )}
    </div>
  );
};

export default GuydeezCoins;
